import { Box, Button, CircularProgress, FormControl, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Paper, Select, styled, TextField, Typography, Tab, Tabs } from '@mui/material';
import React, { useEffect, useState } from 'react'
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import useUserId from 'hooks/useUserId';
import colors from 'colors/color';
import edit_icon from "../../assets/img/edit_icon.svg";
import cancel_icon from "../../assets/img/cancel_icon.svg";
import opened_eye_icon from '../../assets/img/open_eye_icon.svg';
import closed_eye_icon from '../../assets/img/closed_eye_icon.svg';
import { NewSearchButton } from 'components/Buttons/Buttons';
import CameraAltIcon from "@mui/icons-material/CameraAlt";

const CancelButton = styled(Button)(({ theme }) => ({
    width: 115,
    height: 48,
    borderRadius: '10px',
    borderWidth: '1px',
    gap: '10px',
    paddingTop: '14px',
    paddingRight: '24px',
    paddingBottom: '14px',
    paddingLeft: '24px',
    border: '1px solid #FF0000',
    color: '#FF0000',
    "&:hover": {
        backgroundColor: "#FF0000",
        color: "#fff",
    },
}));

const ProfileCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    backgroundColor: "#fff",
    borderRadius: "15px",
    width: "80%",
    margin: "0 auto",
    maxWidth: "1200px",
    marginBottom: "30px",
}));

const StyledTextField = styled(TextField)(({ theme, isediting }) => ({
    "& .MuiOutlinedInput-root": {
        height: 56,
        borderRadius: "10px",
        fontFamily: "Poppins",
        borderColor: "#00000033",
        backgroundColor: "#fff",
        ...(isediting === "true" && {
            "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#e0e0e0",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "transparent",
                borderWidth: "2px",
                boxShadow: "0px 0px 5px 0.4px #0085FF",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "transparent",
                borderWidth: "2px",
                boxShadow: "0px 0px 5px 0.4px #0085FF",
            },
        }),
        ...(isediting === "false" && {
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#00000033",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#00000033",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "transparent",
            },
        }),
    },
}));

const StyledTypography = styled(Typography)(() => ({
    color: "#000",
    fontFamily: 'Poppins',
    fontWeight: 500,
    fontSize: '14px',
}));

const StyledSelect = styled(Select)(({ theme, isediting }) => ({
    height: 56,
    fontFamily: "Poppins",
    borderRadius: "10px",
    ...(isediting === "true" && {
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#e0e0e0",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "transparent",
            borderWidth: "2px",
            boxShadow: "0px 0px 5px 0.4px #0085FF",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "transparent",
            borderWidth: "2px",
            boxShadow: "0px 0px 5px 0.4px #0085FF",
        },
    }),
    ...(isediting === "false" && {
        backgroundColor: "#fff",
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#00000033",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#00000033",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "transparent",
        },
    }),
    "& .MuiSelect-select": {
        padding: "16.5px 14px",
    },
}));

const StyledTextArea = styled(TextField)(({ theme, isediting }) => ({
    "& .MuiOutlinedInput-root": {
        borderRadius: "10px",
        backgroundColor: isediting === "false" ? "#f5f5f5" : "#fff",
        ...(isediting === "true" && {
            "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#e0e0e0",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "transparent",
                borderWidth: "2px",
                boxShadow: "0px 0px 5px 0.4px #0085FF",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "transparent",
                borderWidth: "2px",
                boxShadow: "0px 0px 5px 0.4px #0085FF",
            },
        }),
        ...(isediting === "false" && {
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#00000033",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#00000033",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "transparent",
            },
        }),
        "&.Mui-disabled": {
            backgroundColor: "#fff",
        },
    },
    "& .MuiInputBase-inputMultiline": {
        padding: "16px",
        lineHeight: "1.5",
        fontSize: "1rem",
        color: "#333",
    },
}));

const StyledDatePicker = styled(DatePicker)(({ theme, isediting }) => ({
    "& .MuiOutlinedInput-root": {
        height: 56,
        borderRadius: "10px",
        fontFamily: "Poppins",
        ...(isediting === "true" && {
            "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#e0e0e0",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "transparent",
                borderWidth: "2px",
                boxShadow: "0px 0px 5px 0.4px #0085FF",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "transparent",
                borderWidth: "2px",
                boxShadow: "0px 0px 5px 0.4px #0085FF",
            },
        }),
        ...(isediting === "false" && {
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#00000033",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#00000033",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "transparent",
            },
        }),
    },
}));

function CombinedProfileSettings({ formData, setFormData, handleImageUpload, fileInputRef, handleImageClick }) {
    const { t } = useTranslation();
    const [isEditingPersonalInfo, setIsEditingPersonalInfo] = useState(false);
    const [isEditingBiography, setIsEditingBiography] = useState(false);
    const [isEditingPassword, setIsEditingPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const userId = useUserId();
    const user = userId.userId;
    
    // Add states for password visibility
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Add states for password values
    const [passwordValues, setPasswordValues] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
    });

    const [authMethod, setAuthMethod] = useState(null);
    const [has_password, setHasPassword] = useState(false);

    const showSnackbar = (message, severity = "info") => {
        // This would typically be implemented with a snackbar context
        console.log("Snackbar:", message, severity);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Handle password fields separately
        if (["currentPassword", "newPassword", "confirmPassword"].includes(name)) {
            setPasswordValues((prev) => ({
                ...prev,
                [name]: value,
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    };

    const handleDateChange = (newValue) => {
        const validatedDate = newValue && dayjs.isDayjs(newValue) ? newValue : null;

        setFormData((prev) => ({
            ...prev,
            dateOfBirth: validatedDate,
        }));
    };

    // Toggle password visibility handlers
    const toggleCurrentPasswordVisibility = () => {
        setShowCurrentPassword(!showCurrentPassword);
    };

    const toggleNewPasswordVisibility = () => {
        setShowNewPassword(!showNewPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const checkAuthMethod = async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}authentication/check-auth-method/`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
                    }
                }
            );
            const data = await response.json();
            setAuthMethod(data.auth_method);
            setHasPassword(data.has_password);
        } catch (error) {
            console.error("Error checking auth method:", error);
        }
    };

    useEffect(() => {
        checkAuthMethod();
    }, []);

    const handleUpdatePersonalInfo = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}candidates/${userId.userId}/update/`,
                {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
                    },
                    body: JSON.stringify({
                        company: formData.company || null,
                        email: formData.email || null,
                        first_name: formData.firstName || null,
                        last_name: formData.lastName || null,
                        address: formData.address || null,
                        city: formData.city || null,
                        country: formData.country || null,
                        postal_code: formData.postalCode || null,
                        date_of_birth: formData.dateOfBirth
                            ? formData.dateOfBirth.format("YYYY-MM-DD")
                            : null,
                        family_status: formData.familyStatus || null,
                        academic_degree: formData.academicDegree || null,
                        field_of_study: formData.fieldOfStudy || null,
                        last_post_occupied: formData.lastPostOccupied || null,
                        linkedin_profile: formData.linkedInProfile || null,
                        image: formData.image || null,
                        biography: formData.biography || null,
                    }),
                }
            );

            if (response.ok) {
                showSnackbar(t("personalInformation.profile_updated"), "success");
                setIsEditingPersonalInfo(false);
            } else {
                throw new Error("Update failed");
            }
        } catch (error) {
            console.error("Error updating profile:", error);
            showSnackbar(t("personalInformation.profile_update_failed"), "error");
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateBiography = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}candidates/${userId.userId}/update/`,
                {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
                    },
                    body: JSON.stringify({
                        biography: formData.biography || null,
                    }),
                }
            );

            if (response.ok) {
                showSnackbar(t("personalInformation.biography_updated"), "success");
                setIsEditingBiography(false);
            } else {
                throw new Error("Update failed");
            }
        } catch (error) {
            console.error("Error updating biography:", error);
            showSnackbar(t("personalInformation.biography_update_failed"), "error");
        } finally {
            setLoading(false);
        }
    };

    const handleUpdatePassword = async (e) => {
        e.preventDefault();
        setLoading(true);

        // Check if new password and confirm password match
        if (passwordValues.newPassword !== passwordValues.confirmPassword) {
            showSnackbar(t("personalInformation.passwords_do_not_match"), "error");
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}authentication/change-password/`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
                    },
                    body: JSON.stringify({
                        user: user,
                        old_password: passwordValues.currentPassword,
                        new_password: passwordValues.newPassword,
                    }),
                }
            );

            if (response.ok) {
                showSnackbar(t("personalInformation.password_updated"), "success");
                setIsEditingPassword(false);
                setPasswordValues({
                    currentPassword: "",
                    newPassword: "",
                    confirmPassword: ""
                });
            } else {
                const errorData = await response.json();
                throw new Error(errorData.message || t("personalInformation.password_update_failed"));
            }
        } catch (error) {
            console.error("Error updating password:", error);
            showSnackbar(error.message || t("personalInformation.password_update_failed"), "error");
        } finally {
            setLoading(false);
        }
    };

    const handleCancelPersonalInfo = () => {
        setIsEditingPersonalInfo(false);
    };

    const handleCancelBiography = () => {
        setIsEditingBiography(false);
    };

    const handleCancelPassword = () => {
        setIsEditingPassword(false);
        // Reset password fields when cancelling
        setPasswordValues({
            currentPassword: "",
            newPassword: "",
            confirmPassword: ""
        });
    };

    return (
        <div>
            {/* Personal Information Section */}
            <ProfileCard elevation={0}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-between",
                        mb: 4,
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            fontFamily: 'Poppins',
                            fontWeight: 600,
                            fontSize: '32px',
                            color: colors.primary,
                            textTransform: 'none',
                        }}
                    >
                        {t("personalInformation.personal_information")}
                    </Typography>

                    <Button
                        variant="outlined"
                        onClick={() => setIsEditingPersonalInfo(true)}
                        disabled={isEditingPersonalInfo}
                        sx={{
                            width: 135,
                            height: 48,
                            borderRadius: '10px',
                            gap: '10px',
                            paddingTop: '14px',
                            paddingRight: '20px',
                            paddingBottom: '14px',
                            paddingLeft: '20px',
                            backgroundColor: colors.primary,
                            "&:hover": {
                                border: "none",
                                backgroundColor: "rgba(26, 35, 126, 0.04)",
                                "& .MuiTypography-root": {
                                    color: colors.primary,
                                },
                                "& img": {
                                    filter: `brightness(0) saturate(100%) invert(23%) sepia(74%) saturate(1982%) hue-rotate(220deg) brightness(93%) contrast(98%)`
                                }
                            },
                            "&.Mui-disabled": {
                                bgcolor: "#e0e0e0",
                            },
                        }}
                    >
                        <img src={edit_icon} alt="Edit" />
                        <Typography
                            sx={{
                                paddingLeft: 0,
                                fontFamily: 'Poppins',
                                fontWeight: 400,
                                fontSize: '12px',
                                textTransform: 'none',
                                color: 'white',
                            }}
                        >
                            {t("personalInformation.edit")}
                        </Typography>
                    </Button>
                </Box>

                {/* Form Fields */}
                <Grid
                    container
                    columnSpacing={5}
                    rowSpacing={6}
                    sx={{
                        pl: 5,
                        pr: 5,
                    }}
                >
                    {Object.entries(formData)
                        .filter(
                            ([key]) =>
                                ![
                                    "image",
                                    "academicDegree",
                                    "fieldOfStudy",
                                    "lastPostOccupied",
                                    "linkedInProfile",
                                    "dateOfBirth",
                                    "postalCode",
                                    "familyStatus",
                                    "biography",
                                ].includes(key)
                        )
                        .map(([key, value]) => (
                            <Grid item xs={12} sm={4} key={key}>
                                <FormControl fullWidth>
                                    <StyledTypography>
                                        {t(`personalInformation.${key}`)}
                                    </StyledTypography>
                                    <StyledTextField
                                        fullWidth
                                        name={key}
                                        value={value}
                                        onChange={handleInputChange}
                                        disabled={!isEditingPersonalInfo}
                                        variant="outlined"
                                        isediting={isEditingPersonalInfo.toString()}
                                    />
                                </FormControl>
                            </Grid>
                        ))}

                    {/* New Fields */}
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth required>
                            <StyledTypography>
                                {t("personalInformation.academic_degree")}
                            </StyledTypography>
                            <StyledSelect
                                disabled={!isEditingPersonalInfo}
                                id="academicDegree"
                                name="academicDegree"
                                value={formData.academicDegree}
                                onChange={handleInputChange}
                                isediting={isEditingPersonalInfo.toString()}
                            >
                                <MenuItem value="baccalaureat">Baccalauréat</MenuItem>
                                <MenuItem value="deug">DEUG</MenuItem>
                                <MenuItem value="licence">Licence</MenuItem>
                                <MenuItem value="master">Master</MenuItem>
                                <MenuItem value="doctorat">Doctorat</MenuItem>
                                <MenuItem value="ingenieur">Diplôme d'Ingénieur</MenuItem>
                                <MenuItem value="dut">DUT</MenuItem>
                                <MenuItem value="bts">BTS</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                            </StyledSelect>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth required>
                            <StyledTypography>
                                {t("personalInformation.field_of_study")}
                            </StyledTypography>
                            <StyledSelect
                                disabled={!isEditingPersonalInfo}
                                labelId="field-of-study-label"
                                id="fieldOfStudy"
                                name="fieldOfStudy"
                                value={formData.fieldOfStudy}
                                onChange={handleInputChange}
                                isediting={isEditingPersonalInfo.toString()}
                            >
                                <MenuItem value="computer-science">Computer Science</MenuItem>
                                <MenuItem value="business-administration">
                                    Business Administration
                                </MenuItem>
                                <MenuItem value="mechanical-engineering">
                                    Mechanical Engineering
                                </MenuItem>
                                <MenuItem value="electrical-engineering">
                                    Electrical Engineering
                                </MenuItem>
                                <MenuItem value="civil-engineering">Civil Engineering</MenuItem>
                                <MenuItem value="medicine">Medicine</MenuItem>
                                <MenuItem value="law">Law</MenuItem>
                                <MenuItem value="psychology">Psychology</MenuItem>
                                <MenuItem value="economics">Economics</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                            </StyledSelect>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <StyledTypography>
                            {t("personalInformation.last_post_occupied")}
                        </StyledTypography>
                        <StyledTextField
                            disabled={!isEditingPersonalInfo}
                            fullWidth
                            required
                            id="lastPostOccupied"
                            name="lastPostOccupied"
                            value={formData.lastPostOccupied}
                            onChange={handleInputChange}
                            isediting={isEditingPersonalInfo.toString()}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <StyledTypography>
                            {t("personalInformation.linkedin_profile")}
                        </StyledTypography>
                        <StyledTextField
                            disabled={!isEditingPersonalInfo}
                            fullWidth
                            id="linkedInProfile"
                            name="linkedInProfile"
                            value={formData.linkedInProfile}
                            onChange={handleInputChange}
                            isediting={isEditingPersonalInfo.toString()}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <StyledTypography>
                            {t("personalInformation.date_of_birth")}
                        </StyledTypography>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <StyledDatePicker
                                disabled={!isEditingPersonalInfo}
                                fullWidth
                                required
                                value={formData.dateOfBirth}
                                onChange={handleDateChange}
                                format="YYYY-MM-DD"
                                isediting={isEditingPersonalInfo.toString()}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        variant: "outlined",
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <StyledTypography>
                            {t("personalInformation.family_status")}
                        </StyledTypography>
                        <StyledTextField
                            disabled={!isEditingPersonalInfo}
                            fullWidth
                            id="familyStatus"
                            name="familyStatus"
                            value={formData.familyStatus}
                            onChange={handleInputChange}
                            isediting={isEditingPersonalInfo.toString()}
                        />
                    </Grid>
                </Grid>

                {/* Update Button */}
                {isEditingPersonalInfo ? (
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4, gap: 2 }}>
                        <NewSearchButton
                            variant="contained"
                            onClick={handleUpdatePersonalInfo}
                            disabled={!isEditingPersonalInfo || loading}
                        >
                            {loading ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <CircularProgress size={20} color="inherit" />
                                    <span>{t("personalInformation.updating")}</span>
                                </Box>
                            ) : (
                                t("personalInformation.update_profile")
                            )}
                        </NewSearchButton>
                        <CancelButton
                            variant="contained"
                            onClick={handleCancelPersonalInfo}
                            disabled={!isEditingPersonalInfo || loading}
                        >
                            {loading ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <CircularProgress size={20} color="inherit" />
                                    <span>{t("personalInformation.updating")}</span>
                                </Box>
                            ) : (
                                <>
                                    <img src={cancel_icon} alt="Cancel" />
                                    <Typography>
                                        Cancel
                                    </Typography>
                                </>
                            )}
                        </CancelButton>
                    </Box>
                ) : null}
            </ProfileCard>

            {/* Profile Biography Section */}
            <ProfileCard elevation={0}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-between",
                        mb: 4,
                        mt: 4,
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            fontSize: "32px",
                            color: colors.primary,
                            textTransform: "none",
                        }}
                    >
                        {t("personalInformation.profile_biography")}
                    </Typography>

                    <Button
                        variant="outlined"
                        onClick={() => setIsEditingBiography(true)}
                        disabled={isEditingBiography}
                        sx={{
                            width: 135,
                            height: 48,
                            borderRadius: "10px",
                            gap: "10px",
                            paddingTop: "14px",
                            paddingRight: "20px",
                            paddingBottom: "14px",
                            paddingLeft: "20px",
                            backgroundColor: colors.primary,
                            "&:hover": {
                                border: "none",
                                backgroundColor: "rgba(26, 35, 126, 0.04)",
                                "& .MuiTypography-root": {
                                    color: colors.primary,
                                },
                                "& img": {
                                    filter: `brightness(0) saturate(100%) invert(23%) sepia(74%) saturate(1982%) hue-rotate(220deg) brightness(93%) contrast(98%)`,
                                },
                            },
                            "&.Mui-disabled": {
                                bgcolor: "#e0e0e0",
                            },
                        }}
                    >
                        <img src={edit_icon} alt="Edit" />
                        <Typography
                            sx={{
                                paddingLeft: 0,
                                fontFamily: "Poppins",
                                fontWeight: 400,
                                fontSize: "12px",
                                textTransform: "none",
                                color: "white",
                            }}
                        >
                            {t("personalInformation.edit")}
                        </Typography>
                    </Button>
                </Box>

                {/* Biography Field */}
                <Grid
                    container
                    columnSpacing={5}
                    rowSpacing={6}
                    sx={{
                        pl: 5,
                        pr: 5,
                    }}
                >
                    <Grid
                        item
                        xs={12}
                        spacing={5}
                        sx={{ display: "flex", alignItems: "flex-start", gap: 8 }}
                    >
                        <Grid item xs={3}>
                            <StyledTypography>
                                {t("personalInformation.biography")}
                            </StyledTypography>
                        </Grid>
                        <Grid item xs={9}>
                            <StyledTextArea
                                fullWidth
                                multiline
                                rows={6}
                                name="biography"
                                value={formData.biography}
                                onChange={handleInputChange}
                                disabled={!isEditingBiography}
                                placeholder={t("personalInformation.biography_placeholder")}
                                variant="outlined"
                                isediting={isEditingBiography.toString()}
                            />
                        </Grid>
                    </Grid>
                </Grid>

                {/* Update Button */}
                {isEditingBiography ? (
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4, gap: 2 }}>
                        <NewSearchButton
                            variant="contained"
                            onClick={handleUpdateBiography}
                            disabled={!isEditingBiography || loading}
                        >
                            {loading ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <CircularProgress size={20} color="inherit" />
                                    <span>{t("personalInformation.updating")}</span>
                                </Box>
                            ) : (
                                t("personalInformation.update_profile")
                            )}
                        </NewSearchButton>
                        <CancelButton
                            variant="contained"
                            onClick={handleCancelBiography}
                            disabled={!isEditingBiography || loading}
                        >
                            {loading ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <CircularProgress size={20} color="inherit" />
                                    <span>{t("personalInformation.updating")}</span>
                                </Box>
                            ) : (
                                <>
                                    <img src={cancel_icon} alt="Cancel" />
                                    <Typography>
                                        Cancel
                                    </Typography>
                                </>
                            )}
                        </CancelButton>
                    </Box>
                ) : null}
            </ProfileCard>

            {/* Password Settings Section */}
            <ProfileCard elevation={0}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-between",
                        mb: 4,
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            fontFamily: 'Poppins',
                            fontWeight: 600,
                            fontSize: '32px',
                            color: colors.primary,
                            textTransform: 'none',
                        }}
                    >
                        {t('profile.Password settings')}
                    </Typography>

                    <Button
                        variant="outlined"
                        onClick={() => setIsEditingPassword(true)}
                        disabled={isEditingPassword || !has_password}
                        sx={{
                            width: 135,
                            height: 48,
                            borderRadius: '10px',
                            gap: '10px',
                            paddingTop: '14px',
                            paddingRight: '20px',
                            paddingBottom: '14px',
                            paddingLeft: '20px',
                            backgroundColor: colors.primary,
                            "&:hover": {
                                border: "none",
                                backgroundColor: "rgba(26, 35, 126, 0.04)",
                                "& .MuiTypography-root": {
                                    color: colors.primary,
                                },
                                "& img": {
                                    filter: `brightness(0) saturate(100%) invert(23%) sepia(74%) saturate(1982%) hue-rotate(220deg) brightness(93%) contrast(98%)`
                                }
                            },
                            "&.Mui-disabled": {
                                bgcolor: "#e0e0e0",
                            },
                        }}
                    >
                        <img src={edit_icon} alt="Edit" />
                        <Typography
                            sx={{
                                paddingLeft: 0,
                                fontFamily: 'Poppins',
                                fontWeight: 400,
                                fontSize: '12px',
                                textTransform: 'none',
                                color: 'white',
                            }}
                        >
                            {t("personalInformation.edit")}
                        </Typography>
                    </Button>
                </Box>

                {/* Form Fields */}
                <Grid
                    container
                    columnSpacing={5}
                    rowSpacing={6}
                    sx={{
                        pl: 5,
                        pr: 5,
                    }}
                >
                    {/* Current Password */}
                    <Grid item xs={12} spacing={5} sx={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                        <Grid item xs={3}>
                            <StyledTypography>
                                {t('profile.Current password')}
                            </StyledTypography>
                        </Grid>
                        <Grid item xs={4}>
                            <StyledTextField
                                disabled={!isEditingPassword}
                                fullWidth
                                required
                                id="currentPassword"
                                name="currentPassword"
                                type={showCurrentPassword ? 'text' : 'password'}
                                value={passwordValues.currentPassword}
                                onChange={handleInputChange}
                                isediting={isEditingPassword.toString()}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={toggleCurrentPasswordVisibility}
                                                edge="end"
                                                disabled={!isEditingPassword}
                                                sx={{ marginRight: 1 }}
                                            >
                                                <img
                                                    src={showCurrentPassword ? opened_eye_icon : closed_eye_icon}
                                                    alt={showCurrentPassword ? "Hide password" : "Show password"}
                                                    style={{
                                                        width: '15px',
                                                        height: '15px',
                                                        filter: !isEditingPassword ? 'grayscale(100%)' : 'none',
                                                        opacity: !isEditingPassword ? 0.5 : 1
                                                    }}
                                                />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                    </Grid>

                    {/* New Password */}
                    <Grid item xs={12} spacing={5} sx={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                        <Grid item xs={3}>
                            <StyledTypography>
                                {t('profile.New password')}
                            </StyledTypography>
                        </Grid>
                        <Grid item xs={4}>
                            <StyledTextField
                                disabled={!isEditingPassword}
                                fullWidth
                                required
                                id="newPassword"
                                name="newPassword"
                                type={showNewPassword ? 'text' : 'password'}
                                value={passwordValues.newPassword}
                                onChange={handleInputChange}
                                isediting={isEditingPassword.toString()}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={toggleNewPasswordVisibility}
                                                edge="end"
                                                disabled={!isEditingPassword}
                                                sx={{ marginRight: 1 }}
                                            >
                                                <img
                                                    src={showNewPassword ? opened_eye_icon : closed_eye_icon}
                                                    alt={showNewPassword ? "Hide password" : "Show password"}
                                                    style={{
                                                        width: '15px',
                                                        height: '15px',
                                                        filter: !isEditingPassword ? 'grayscale(100%)' : 'none',
                                                        opacity: !isEditingPassword ? 0.5 : 1
                                                    }}
                                                />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                    </Grid>

                    {/* Confirm New Password */}
                    <Grid item xs={12} spacing={5} sx={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                        <Grid item xs={3}>
                            <StyledTypography>
                                {t('profile.Confirm new password')}
                            </StyledTypography>
                        </Grid>
                        <Grid item xs={4}>
                            <StyledTextField
                                disabled={!isEditingPassword}
                                fullWidth
                                required
                                id="confirmPassword"
                                name="confirmPassword"
                                type={showConfirmPassword ? 'text' : 'password'}
                                value={passwordValues.confirmPassword}
                                onChange={handleInputChange}
                                isediting={isEditingPassword.toString()}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={toggleConfirmPasswordVisibility}
                                                edge="end"
                                                disabled={!isEditingPassword}
                                                sx={{ marginRight: 1 }}
                                            >
                                                <img
                                                    src={showConfirmPassword ? opened_eye_icon : closed_eye_icon}
                                                    alt={showConfirmPassword ? "Hide password" : "Show password"}
                                                    style={{
                                                        width: '15px',
                                                        height: '15px',
                                                        filter: !isEditingPassword ? 'grayscale(100%)' : 'none',
                                                        opacity: !isEditingPassword ? 0.5 : 1
                                                    }}
                                                />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                    </Grid>
                </Grid>

                {/* Update Button */}
                {isEditingPassword ? (
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4, gap: 2 }}>
                        <NewSearchButton
                            variant="contained"
                            onClick={handleUpdatePassword}
                            disabled={!isEditingPassword || loading}
                        >
                            {loading ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <CircularProgress size={20} color="inherit" />
                                    <span>{t("personalInformation.updating")}</span>
                                </Box>
                            ) : (
                                t("personalInformation.update_profile")
                            )}
                        </NewSearchButton>

                        <CancelButton
                            variant="contained"
                            onClick={handleCancelPassword}
                            disabled={!isEditingPassword || loading}
                        >
                            {loading ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <CircularProgress size={20} color="inherit" />
                                    <span>{t("personalInformation.updating")}</span>
                                </Box>
                            ) : (
                                <>
                                    <img src={cancel_icon} alt="Cancel" />
                                    <Typography>
                                        Cancel
                                    </Typography>
                                </>
                            )}
                        </CancelButton>
                    </Box>
                ) : null}
            </ProfileCard>
        </div>
    )
}

export default CombinedProfileSettings;